import React, { ChangeEvent, useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMaintenanceTicketByIdRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { mapMaintenanceTicketToLegacy } from '../../hooks/maintenance/query/V2/maintenanceMappers';
import { updateMaintenanceTicket } from '../../api/maintenance';
import useSystemAdmins from '../../hooks/system/query/useSystemAdmins';
import { getPriorityName, history, isEmptyObject, toTitleCase } from '../../utils';
import { IAdmin } from '../../ts/interface/admins.interface';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { ITicket } from '../../ts/interface/problem.interface';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import SaveIcon from '@mui/icons-material/Save';
import {
  MovaticFieldGroupDate,
  MovaticFieldGroupSelect,
} from '../../components/Redesing/movatic-field-group';
import CustomToast from '../../components/Redesing/custom-toast';
import MenuItem from '@mui/material/MenuItem';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import { Stack } from '@mui/system';
import CardView from '../../components/Redesing/card-view';
import PageContainer from '../../components/Redesing/page-container';
import useOrganization from '../../hooks/use-organization';
import TicketImageManager, {
  TicketImageManagerRef,
} from '../../components/Maintenance/TicketImageManager';

interface IEditTicketState {
  assignee: string;
  priority: string;
  due_on: string;
  dateValidation: string | null;
}

const EditTicket = ({
  mode,
  system,
  loaded,
}: {
  mode: IMode;
  system: ISystem;
  loaded: boolean;
}) => {
  const organizationInfo = useOrganization();

  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const initialState = {
    assignee: '',
    priority: '',
    due_on: '',
    dateValidation: null,
  };
  const [{ assignee, priority, due_on, dateValidation }, setState] =
    useState<IEditTicketState>(initialState);

  const { data: ticketRaw, isLoading } = useMaintenanceTicketByIdRelayQuery(ticketId, {
    enabled: loaded || Boolean(organizationInfo),
  });

  const ticket = useMemo(
    () => (ticketRaw ? mapMaintenanceTicketToLegacy(ticketRaw) : ({} as ITicket)),
    [ticketRaw]
  );

  const { data: admins = [] } = useSystemAdmins(
    {
      enabled: loaded || Boolean(organizationInfo),
    },
    {
      organization_id: organizationInfo?.id,
    }
  );

  const imageManagerRef = useRef<TicketImageManagerRef>(null);

  useEffect(() => {
    if (!isEmptyObject(ticket)) {
      setState((prevState) => ({
        ...prevState,
        assignee: ticket.assignee || '',
        priority: ticket.priority,
        due_on: new Date(ticket.due_on).toISOString(),
      }));
    }
  }, [ticket]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (date: Date) => {
    if (date.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      setState((prevState) => ({
        ...prevState,
        dateValidation: 'error',
      }));
      CustomToast({
        type: 'error',
        message: 'Target Date must be a future date',
      });
    } else {
      setState((prevState) => ({
        ...prevState,
        due_on: new Date(date).toISOString(),
        dateValidation: null,
      }));
    }
  };

  const save = async () => {
    if (
      due_on &&
      new Date(due_on).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
    ) {
      CustomAsyncToast({
        promise: async () => {
          await updateMaintenanceTicket({
            id: ticket?.old_id,
            assignee: assignee && assignee !== 'None' ? assignee : null,
            priority: Number(priority),
            due_on: due_on,
          });

          // Then upload any pending images
          if (imageManagerRef.current) {
            const uploadSuccess = await imageManagerRef.current.uploadImages();
            if (!uploadSuccess) {
              CustomToast({
                type: 'error',
                message: 'Ticket updated but some images failed to upload',
              });
            }
          }
        },
        successMessage: () => 'Ticket updated successfully',
        loadingMessage: 'Updating ticket...',
        errorMessage: 'Unable to update ticket',
      }).then(() => {
        history.push(`/maintenance/${ticketId}`);
      });
    } else {
      setState((prevState) => ({
        ...prevState,
        dateValidation: 'error',
      }));
      CustomToast({
        type: 'error',
        message: 'Target Date must be a future date',
      });
    }
  };
  const getAdmins = () => {
    if (admins.length > 0) {
      const adminOptions = admins.map((admin: IAdmin) => ({
        value: admin.email,
        name: `${admin.firstName} ${admin.lastName}`,
      }));
      adminOptions.unshift({ value: '', name: 'None' });
      return adminOptions;
    }
    return [{ value: '', name: 'None' }];
  };

  const getHeader = (ticket: ITicket) => {
    let header = 'Edit Ticket for ';
    // TODO: remove this
    if (system.new_hardware) {
      if (ticket?.hardware_number) {
        header += `${toTitleCase(ticket?.hardware_type_name)} ${ticket?.hardware_number}`;
      } else {
        header += ticket[ticket?.primary_identifier];
      }
    } else {
      header += `${mode.details.unitsTitle} ${ticket?.hardware_number}`;
    }
    return header;
  };

  return (
    <PageContainer isLoading={isLoading}>
      <CardView
        content={
          <Stack px={2} pb={2}>
            <MovaticFieldGroupDate
              id="archived_on"
              label="Target Date"
              description="Target Date for this ticket."
              value={new Date(due_on) || null}
              hasError={dateValidation === 'error'}
              onChange={handleDateChange}
            />

            <MovaticFieldGroupSelect
              id="priority"
              label="Priority"
              description="Priority of this ticket."
              optionNodes={[
                { value: 1, name: `1 - ${getPriorityName(1)}` },
                { value: 2, name: `2 - ${getPriorityName(2)}` },
                { value: 3, name: `3 - ${getPriorityName(3)}` },
                { value: 4, name: `4 - ${getPriorityName(4)}` },
                { value: 5, name: `5 - ${getPriorityName(5)}` },
              ].map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
              value={priority || 3}
              onChange={handleChange}
              name="priority"
            />

            <MovaticFieldGroupSelect
              id="assignee"
              label="Assignee"
              description="Priority of this ticket."
              optionNodes={getAdmins().map(
                (option: { name: string; value: string }, index: number) => (
                  <MenuItem key={index} value={option.value}>
                    {option.name}
                  </MenuItem>
                )
              )}
              value={assignee}
              onChange={handleChange}
              name="assignee"
            />

            <TicketImageManager
              ref={imageManagerRef}
              ticketId={ticket?.gid || ''}
              systemId={ticket?.system_info?.old_id}
              existingImages={ticket?.images || []}
              readOnly={false}
            />
          </Stack>
        }
        title={getHeader(ticket)}
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => history.push(`/maintenance/${ticketId}`),
              },
              {
                label: 'Save',
                onClick: save,
                startIcon: <SaveIcon />,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: { system: { current: ISystem; isLoaded: boolean }; mode: IMode }) => ({
    system: state.system.current,
    loaded: state.system.isLoaded,
    mode: state.mode,
  }),
  () => ({})
)(EditTicket);
